const IS_WITH_PROD = import.meta.env.MODE === 'dev-with-prod'
const IS_WITH_STAGING = import.meta.env.MODE === 'dev-with-staging'

type EnvName = 'production' | 'staging' | 'development' | 'test'

interface BibeInfo {
  bibeNumber: string
  isProduction: boolean
}

function getBibeInfo(): BibeInfo | null {
  if (typeof window === 'undefined') {
    return null
  }
  const hostname = window.location.hostname

  // Match number and domain
  const prodPattern = /^(\d+)\.prod-bibes\./
  const stagingPattern = /^(\d+)\.staging-bibes\./

  const prodMatch = hostname.match(prodPattern)
  if (prodMatch) {
    return {
      bibeNumber: prodMatch[1],
      isProduction: true,
    }
  }

  const stagingMatch = hostname.match(stagingPattern)
  if (stagingMatch) {
    return {
      bibeNumber: stagingMatch[1],
      isProduction: false,
    }
  }

  return null
}

function getEnvFromBibeOrHostname(): EnvName {
  const hostname = window.location.hostname
  const bibeInfo = getBibeInfo()

  if (bibeInfo) {
    return bibeInfo.isProduction ? 'production' : 'staging'
  }

  if (hostname.includes('.dev')) {
    return 'staging'
  }

  if (hostname.includes('.local')) {
    return 'development'
  }

  return 'production'
}

function getBibeEndpoint(): string | null {
  const bibeInfo = getBibeInfo()

  if (bibeInfo) {
    const bibeEnvironment = bibeInfo.isProduction
      ? 'prod-bibes'
      : 'staging-bibes'
    const graphDomain = bibeInfo.isProduction
      ? 'graph.buffer.com'
      : 'graph.dev.buffer.com'

    return `https://${bibeInfo.bibeNumber}.${bibeEnvironment}.${graphDomain}`
  }

  return null
}

export type Env = {
  VITE_NODE_ENV: EnvName
  VITE_COMMIT_HASH: string
  VITE_SPLIT_ENV: string
  VITE_SEGMENT_KEY: string
  VITE_GRAPHQL_API: string
  VITE_GRAPHQL_MOCK_API: string
  PUSHER_KEY: string
  PUSHER_CLUSTER: string
  VITE_X_BUFFER_CLIENT_ID: string
  VITE_CUSTOMERIO_SITE_ID: string
  VITE_CUSTOMERIO_API_KEY: string
}

const COMMON_VARS: Pick<Env, 'VITE_COMMIT_HASH' | 'VITE_X_BUFFER_CLIENT_ID'> = {
  VITE_COMMIT_HASH: `${import.meta.env.VITE_COMMIT_HASH}`,
  VITE_X_BUFFER_CLIENT_ID: 'engage',
}

const PRODUCTION_VARS: Env = {
  ...COMMON_VARS,
  VITE_NODE_ENV: 'production',
  VITE_SPLIT_ENV: 'production',
  VITE_SEGMENT_KEY: 'xGr8dWg9LQjpUzfNByAXEx0M4WRcqCA0',
  VITE_GRAPHQL_API: getBibeEndpoint() ?? 'https://graph.buffer.com',
  VITE_GRAPHQL_MOCK_API: 'https://mock-graph.dev.buffer.com/',
  PUSHER_KEY: '8bdc949a902712551291',
  PUSHER_CLUSTER: 'us2',
  VITE_CUSTOMERIO_SITE_ID: 'f902d427753d24b6e6df',
  VITE_CUSTOMERIO_API_KEY: '61042496ac16f1624aa9',
}

const STAGING_VARS: Env = {
  ...COMMON_VARS,
  VITE_NODE_ENV: 'staging',
  VITE_SPLIT_ENV: 'staging',
  VITE_SEGMENT_KEY: 'nNTkRrDuA0dkbBVWqPaa4yhqUpL8wzbm',
  VITE_GRAPHQL_API: getBibeEndpoint() ?? 'https://graph.dev.buffer.com',
  VITE_GRAPHQL_MOCK_API: 'https://mock-graph.dev.buffer.com/',
  PUSHER_KEY: 'c782f72683b675cfac93',
  PUSHER_CLUSTER: 'us2',
  VITE_CUSTOMERIO_SITE_ID: '7c16d71a254f8ac07907',
  VITE_CUSTOMERIO_API_KEY: '9990a619b68d4cf134a1',
}

const DEVELOPMENT_VARS: Env = {
  ...STAGING_VARS,
  ...COMMON_VARS,
  VITE_NODE_ENV: 'development',
  VITE_SPLIT_ENV: 'local',
  VITE_SEGMENT_KEY: 'nNTkRrDuA0dkbBVWqPaa4yhqUpL8wzbm',
  VITE_GRAPHQL_API: getBibeEndpoint() ?? 'https://graph.local.buffer.com',
  VITE_GRAPHQL_MOCK_API: 'https://graph.local.buffer.com',
  PUSHER_KEY: 'c782f72683b675cfac93',
  PUSHER_CLUSTER: 'us2',
  VITE_CUSTOMERIO_SITE_ID: '82d38857f6dae390778e',
  VITE_CUSTOMERIO_API_KEY: '63abb1784168503cb485',
}

function selectEnv(): Env {
  const envName = getEnvFromBibeOrHostname()

  if (IS_WITH_PROD) {
    return {
      ...PRODUCTION_VARS,
      VITE_NODE_ENV: 'development',
    }
  }
  if (IS_WITH_STAGING) {
    return {
      ...STAGING_VARS,
      VITE_NODE_ENV: 'development',
    }
  }
  if (envName === 'development') {
    return DEVELOPMENT_VARS
  }
  if (envName === 'staging') {
    return STAGING_VARS
  }

  return PRODUCTION_VARS
}

export const env = selectEnv()
